import getConfig from 'next/config'
import { useRouter } from 'next/router'
import Script from 'next/script'
import React, { createContext, FC, useCallback, useMemo, useState } from 'react'

interface RecaptchaContextProps {
  executeRecaptcha: (action: string) => Promise<string>
}

interface RecaptchaContextProviderProps {
  children: React.JSX.Element
}

export const RecaptchaContext = createContext<RecaptchaContextProps | null>(null)

export const RecaptchaContextProvider: FC<RecaptchaContextProviderProps> = ({ children }) => {
  const [recaptchaInstance, setRecaptchaInstance] = useState<any>(null)

  const { locale } = useRouter()
  const { publicRuntimeConfig } = getConfig()

  const handleScriptLoad = () => {
    if (typeof window === 'undefined') { return }

    if ((window as any).grecaptcha) {
      setRecaptchaInstance((window as any).grecaptcha)
    } else {
      console.error('Failed to load reCAPTCHA client')
    }
  }

  const executeRecaptcha = useCallback(
    async (action: string): Promise<string> => {
      if (!recaptchaInstance) {
        throw new Error('reCAPTCHA not initialized')
      }
      return recaptchaInstance.execute(publicRuntimeConfig.recaptchaSiteKey, { action })
    },
    [recaptchaInstance, publicRuntimeConfig.recaptchaSiteKey],
  )

  const contextValue = useMemo(
    () => ({ executeRecaptcha }),
    [executeRecaptcha],
  )

  // recaptchaEnabled controls which locales have reCAPTCHA enabled
  // Add locales to this array and make sure the featureflag is enabled on graph to enable validation.
  const recaptchaEnabled: string[] = []
  if (!locale || !recaptchaEnabled.includes(locale)) {
    return (children)
  }

  return (
    <>
      <Script id="recaptcha" onLoad={handleScriptLoad} src={`https://www.google.com/recaptcha/api.js?render=${publicRuntimeConfig.recaptchaSiteKey}`} strategy="lazyOnload" />
      <RecaptchaContext.Provider value={contextValue}>
        {children}
      </RecaptchaContext.Provider>
    </>
  )
}

export default RecaptchaContextProvider
